import { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { login, signup, verifyEmail, resendCode, forgotPassword, resetPasswordVerify, resetPasswordConfirm } from "./client";
import { ErrorBanner, FieldError, useFormErrors } from "./FormError";
import { useI18n, LANGUAGES } from "./i18n";
import { trackPageView } from "./uxTracker";

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeIn  = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const pop     = keyframes`0%{transform:scale(.9)}60%{transform:scale(1.05)}100%{transform:scale(1)}`;

/* ─── Styled components ──────────────────────────────────────────────────── */
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,.8);backdrop-filter:blur(8px);z-index:300;display:flex;align-items:center;justify-content:center;padding:1rem;animation:${fadeIn} .15s ease;`;
const Modal = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.borderLight};border-radius:16px;width:100%;max-width:390px;padding:2rem;display:flex;flex-direction:column;gap:1.25rem;animation:${slideUp} .2s ease;max-height:90vh;max-height:90dvh;overflow-y:auto;&>*{flex-shrink:0;}`;
const Head = styled.div`display:flex;justify-content:space-between;align-items:flex-start;`;
const ModalTitle = styled.h2`font-family:${({theme})=>theme.fontDisplay};font-size:1.4rem;font-weight:700;letter-spacing:-.02em;span{color:${({theme})=>theme.green};}`;
const ModalSub = styled.p`font-size:.75rem;color:${({theme})=>theme.textMuted};margin-top:.25rem;`;
const CloseBtn = styled.button`background:none;border:none;color:${({theme})=>theme.textMuted};cursor:pointer;font-size:1.1rem;&:hover{color:${({theme})=>theme.text};}`;
const TabRow = styled.div`display:grid;grid-template-columns:1fr 1fr;border:1px solid ${({theme})=>theme.border};border-radius:8px;overflow:hidden;`;
const Tab = styled.button`padding:.55rem;border:none;font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:600;cursor:pointer;background:${({$active,theme})=>$active?theme.bgHover:"transparent"};color:${({$active,theme})=>$active?theme.text:theme.textMuted};border-bottom:2px solid ${({$active,theme})=>$active?theme.green:"transparent"};transition:all .15s;`;
const GoogleBtn = styled.button`width:100%;display:flex;align-items:center;justify-content:center;gap:.65rem;padding:.7rem 1rem;border-radius:8px;border:1px solid ${({theme})=>theme.borderLight};background:${({theme})=>theme.bg};color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};font-size:.85rem;font-weight:600;cursor:pointer;transition:all .15s;&:hover{background:${({theme})=>theme.bgHover};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const Separator = styled.div`display:flex;align-items:center;gap:.75rem;color:${({theme})=>theme.textMuted};font-size:.72rem;&::before,&::after{content:'';flex:1;height:1px;background:${({theme})=>theme.border};}`;
const Form = styled.div`display:flex;flex-direction:column;gap:.75rem;`;
const Field = styled.div`display:flex;flex-direction:column;gap:.3rem;`;
const FieldLabel = styled.label`font-size:.68rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const FieldInput = styled.input`background:${({theme})=>theme.bg};border:1px solid ${({$err,theme})=>$err?theme.red+"88":theme.border};border-radius:8px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontMono};font-size:.85rem;padding:.6rem .85rem;outline:none;transition:border-color .15s;&:focus{border-color:${({$err,theme})=>$err?theme.red:theme.borderLight};}&::placeholder{color:${({theme})=>theme.textMuted};font-family:${({theme})=>theme.fontBody};}`;
const PasswordWrapper = styled.div`position:relative;display:flex;align-items:center;`;
const PasswordInput   = styled(FieldInput)`width:100%;padding-right:2.5rem;`;
const EyeBtn = styled.button`position:absolute;right:.7rem;background:none;border:none;color:${({theme})=>theme.textMuted};cursor:pointer;font-size:.9rem;padding:0;line-height:1;transition:color .15s;&:hover{color:${({theme})=>theme.text};}`;
const SubmitBtn = styled.button`width:100%;padding:.8rem;border-radius:8px;border:none;background:${({theme})=>theme.green};color:#000;font-family:${({theme})=>theme.fontBody};font-size:.85rem;font-weight:700;letter-spacing:.04em;cursor:pointer;transition:opacity .15s;&:hover{opacity:.9;}&:disabled{opacity:.4;cursor:not-allowed;}`;
const CheckRow  = styled.label`display:flex;align-items:flex-start;gap:.65rem;cursor:pointer;padding:.1rem 0;`;
const CheckBox  = styled.input`width:16px;height:16px;margin-top:2px;flex-shrink:0;accent-color:${({theme})=>theme.green};cursor:pointer;`;
const CheckText = styled.span`font-size:.73rem;color:${({theme})=>theme.textDim};line-height:1.5;a{color:${({theme})=>theme.accent};text-decoration:underline;cursor:pointer;&:hover{opacity:.8;}}&.required::after{content:' *';color:${({theme})=>theme.red};}`;
const CheckGroup = styled.div`display:flex;flex-direction:column;gap:.6rem;padding:.25rem 0;`;
const EVPBonusBanner = styled.div`background:rgba(0,230,118,.07);border:1px solid rgba(0,230,118,.25);border-radius:10px;padding:.7rem 1rem;display:flex;align-items:center;gap:.6rem;font-size:.75rem;color:${({theme})=>theme.textDim};line-height:1.45;strong{color:${({theme})=>theme.green};}`;

/* ── Verify / Forgot shared ─────────────────────────────────────────────── */
const VerifyWrap  = styled.div`display:flex;flex-direction:column;gap:1.25rem;animation:${slideUp} .2s ease;`;
const VerifyIcon  = styled.div`font-size:2.8rem;text-align:center;animation:${pop} .4s ease;`;
const VerifyTitle = styled.h3`font-family:${({theme})=>theme.fontDisplay};font-size:1.1rem;font-weight:700;text-align:center;`;
const VerifyDesc  = styled.p`font-size:.78rem;color:${({theme})=>theme.textMuted};text-align:center;line-height:1.6;`;
const EmailChip   = styled.div`background:${({theme})=>theme.bgHover};border:1px solid ${({theme})=>theme.borderLight};border-radius:8px;padding:.6rem 1rem;text-align:center;font-family:${({theme})=>theme.fontMono};font-size:.8rem;color:${({theme})=>theme.text};`;
const StepList    = styled.ol`list-style:none;display:flex;flex-direction:column;gap:.55rem;padding:0;margin:0;`;
const StepItem    = styled.li`display:flex;align-items:flex-start;gap:.6rem;font-size:.77rem;color:${({theme})=>theme.textDim};line-height:1.5;`;
const StepNum     = styled.span`width:20px;height:20px;border-radius:50%;border:1px solid ${({theme})=>theme.borderLight};font-family:${({theme})=>theme.fontMono};font-size:.65rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:${({theme})=>theme.accent};margin-top:1px;`;
const ResendBtn   = styled.button`background:none;border:none;color:${({theme})=>theme.accent};font-size:.72rem;cursor:pointer;font-family:inherit;text-decoration:underline;padding:0;&:disabled{opacity:.4;cursor:not-allowed;}`;
const CountDown   = styled.span`font-family:'DM Mono',monospace;font-size:.7rem;color:${({theme})=>theme.textMuted};`;
const ForgotLink  = styled.button`background:none;border:none;color:${({theme})=>theme.accent};font-size:.72rem;cursor:pointer;font-family:inherit;text-decoration:underline;padding:0;text-align:right;align-self:flex-end;margin-top:-.25rem;&:hover{opacity:.8;}`;
const PasswordStrengthBar = styled.div`height:3px;border-radius:2px;background:${({$l})=>$l>=4?"#00e676":$l>=3?"#00e676":$l>=2?"#ffc107":"#ff3b5c"};width:${({$l})=>$l*25}%;transition:all .3s;margin-top:.3rem;`;
const BackLink = styled.button`background:none;border:none;color:#5a6a7a;font-size:.72rem;cursor:pointer;font-family:inherit;text-decoration:underline;text-align:center;`;
const LangRow = styled.div`display:flex;align-items:center;justify-content:center;gap:.35rem;flex-wrap:wrap;padding-top:.25rem;`;
const LangBtn = styled.button`padding:.2rem .45rem;border-radius:5px;border:1px solid ${({$a,theme})=>$a?theme.green+"88":theme.border};background:${({$a,theme})=>$a?theme.greenDim:"transparent"};color:${({$a,theme})=>$a?theme.green:theme.textMuted};font-family:${({theme})=>theme.fontMono};font-size:.6rem;cursor:pointer;transition:all .15s;&:hover{color:${({theme})=>theme.text};}`;

/* ─── Turnstile ─────────────────────────────────────────────────────────── */
const TURNSTILE_SITE_KEY = "b830da44f679";
const TurnstileWrap   = styled.div`display:flex;justify-content:center;iframe{border-radius:8px;overflow:hidden;}`;
const TurnstileNotice = styled.div`font-size:.68rem;color:${({theme})=>theme.textMuted};text-align:center;padding:.35rem;background:rgba(255,193,7,.05);border:1px solid rgba(255,193,7,.2);border-radius:6px;`;

function loadTurnstileScript() {
  return new Promise((resolve) => {
    if (window.turnstile) { resolve(); return; }
    if (document.getElementById("cf-turnstile-script")) {
      const check = setInterval(() => { if (window.turnstile) { clearInterval(check); resolve(); } }, 100);
      return;
    }
    const s = document.createElement("script");
    s.id = "cf-turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true; s.defer = true; s.onload = resolve;
    document.head.appendChild(s);
  });
}

function TurnstileWidget({ onVerified, onError, resetKey }) {
  const containerRef = useRef(null);
  const widgetId     = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      if (widgetId.current != null) { try { window.turnstile.remove(widgetId.current); } catch {} }
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY, theme: "dark", size: "normal",
        callback: (token)  => { if (!cancelled) onVerified(token); },
        "error-callback":   () => { if (!cancelled) onError?.("Vérification échouée."); },
        "expired-callback": () => { if (!cancelled) onVerified(""); },
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current != null) { try { window.turnstile.remove(widgetId.current); } catch {} widgetId.current = null; }
    };
  }, [resetKey]);

  if (!TURNSTILE_SITE_KEY) return (
    <TurnstileNotice>⚠ Ajoutez <code style={{fontFamily:"DM Mono,monospace",color:"#4fc3f7"}}>REACT_APP_TURNSTILE_SITE_KEY</code> dans votre <code style={{fontFamily:"DM Mono,monospace",color:"#4fc3f7"}}>.env</code>.</TurnstileNotice>
  );
  return <TurnstileWrap><div ref={containerRef}/></TurnstileWrap>;
}

/* ─── Password strength ─────────────────────────────────────────────────── */
function getStrength(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^a-zA-Z0-9]/.test(p)) s++;
  return Math.min(4, s);
}

/* ─── Google ─────────────────────────────────────────────────────────────── */
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (document.getElementById("google-gsi-script")) { resolve(); return; }
    const s = document.createElement("script");
    s.id = "google-gsi-script"; s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}
async function loginWithGoogle() {
  await loadGoogleScript();
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) { reject(new Error("Google SDK non chargé.")); return; }
    try {
      window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID, scope: "openid email profile", ux_mode: "popup",
        callback: (res) => res.error ? reject(new Error(res.error)) : resolve(res.code),
        // Appelé quand la popup est fermée sans terminer (popup_closed) ou bloquée (popup_failed_to_open).
        // Sans ce handler, la promesse ne se résout jamais et l'écran reste en « Connexion Google… ».
        error_callback: (err) => reject(new Error(err?.type || "popup_closed")),
      }).requestCode();
    } catch (e) { reject(e); }
  });
}
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════════════
   TRANSLATIONS
═══════════════════════════════════════════════════════════════════════════ */
const TX = {
  /* modal subtitles */
  "sub.login":       { fr:"Bon retour sur la plateforme",              en:"Welcome back",                         es:"Bienvenido de vuelta",                  de:"Willkommen zurück",                     pt:"Bem-vindo de volta",                    ar:"مرحباً بعودتك"                     },
  "sub.signup":      { fr:"Créez votre compte gratuit",                en:"Create your free account",             es:"Crea tu cuenta gratuita",               de:"Erstellen Sie Ihr kostenloses Konto",    pt:"Crie sua conta grátis",                 ar:"أنشئ حسابك المجاني"                },
  "sub.verify":      { fr:"Confirmez votre identité",                  en:"Confirm your identity",                es:"Confirma tu identidad",                 de:"Bestätigen Sie Ihre Identität",         pt:"Confirme sua identidade",               ar:"تأكيد هويتك"                       },
  "sub.forgot":      { fr:"Réinitialisation du mot de passe",          en:"Password reset",                       es:"Restablecimiento de contraseña",        de:"Passwort zurücksetzen",                 pt:"Redefinição de senha",                  ar:"إعادة تعيين كلمة المرور"           },
  "sub.forgot-code": { fr:"Code de réinitialisation",                  en:"Reset code",                          es:"Código de restablecimiento",            de:"Zurücksetzungscode",                    pt:"Código de redefinição",                 ar:"رمز إعادة التعيين"                 },
  "sub.forgot-pwd":  { fr:"Nouveau mot de passe",                      en:"New password",                        es:"Nueva contraseña",                      de:"Neues Passwort",                        pt:"Nova senha",                            ar:"كلمة مرور جديدة"                   },
  /* tabs */
  "tab.login":       { fr:"Se connecter",                              en:"Log in",                               es:"Iniciar sesión",                        de:"Anmelden",                              pt:"Entrar",                                ar:"تسجيل الدخول"                      },
  "tab.signup":      { fr:"S'inscrire",                                en:"Sign up",                              es:"Registrarse",                           de:"Registrieren",                          pt:"Cadastrar",                             ar:"إنشاء حساب"                        },
  /* google */
  "google.login":    { fr:"Continuer avec Google",                     en:"Continue with Google",                 es:"Continuar con Google",                  de:"Mit Google fortfahren",                 pt:"Continuar com Google",                  ar:"المتابعة مع Google"                },
  "google.signup":   { fr:"S'inscrire avec Google",                    en:"Sign up with Google",                  es:"Registrarse con Google",                de:"Mit Google registrieren",               pt:"Cadastrar com Google",                  ar:"التسجيل مع Google"                 },
  "google.loading":  { fr:"Connexion Google…",                         en:"Connecting to Google…",                es:"Conectando con Google…",                de:"Google-Verbindung…",                    pt:"Conectando ao Google…",                 ar:"جارٍ الاتصال بـ Google…"           },
  /* separator */
  "or":              { fr:"ou",                                        en:"or",                                   es:"o",                                     de:"oder",                                  pt:"ou",                                    ar:"أو"                                },
  /* fields */
  "f.email":         { fr:"Email",                                     en:"Email",                                es:"Correo electrónico",                    de:"E-Mail",                                pt:"E-mail",                                ar:"البريد الإلكتروني"                 },
  "f.email.ph":      { fr:"vous@exemple.com",                          en:"you@example.com",                      es:"tú@ejemplo.com",                        de:"sie@beispiel.de",                       pt:"voce@exemplo.com",                      ar:"بريدك@مثال.com"                    },
  "f.password":      { fr:"Mot de passe",                              en:"Password",                             es:"Contraseña",                            de:"Passwort",                              pt:"Senha",                                 ar:"كلمة المرور"                       },
  "f.password.ph":   { fr:"••••••••",                                  en:"••••••••",                             es:"••••••••",                              de:"••••••••",                              pt:"••••••••",                              ar:"••••••••"                           },
  "f.pwd.show":      { fr:"Afficher",                                  en:"Show",                                 es:"Mostrar",                               de:"Anzeigen",                              pt:"Mostrar",                               ar:"إظهار"                             },
  "f.pwd.hide":      { fr:"Masquer",                                   en:"Hide",                                 es:"Ocultar",                               de:"Verbergen",                             pt:"Ocultar",                               ar:"إخفاء"                             },
  /* strength */
  "str.1":           { fr:"Faible",                                    en:"Weak",                                 es:"Débil",                                 de:"Schwach",                               pt:"Fraca",                                 ar:"ضعيفة"                             },
  "str.2":           { fr:"Moyen",                                     en:"Fair",                                 es:"Regular",                               de:"Mittel",                                pt:"Regular",                               ar:"متوسطة"                            },
  "str.3":           { fr:"Bon",                                       en:"Good",                                 es:"Buena",                                 de:"Gut",                                   pt:"Boa",                                   ar:"جيدة"                              },
  "str.4":           { fr:"Excellent",                                 en:"Excellent",                            es:"Excelente",                             de:"Ausgezeichnet",                         pt:"Excelente",                             ar:"ممتازة"                            },
    "ref.label":  { fr:"Code de parrainage (optionnel)",
                  en:"Referral code (optional)",
                  es:"Código de referido (opcional)",
                  de:"Empfehlungscode (optional)",
                  pt:"Código de indicação (opcional)",
                  ar:"رمز الإحالة (اختياري)" },
  "ref.ph":     { fr:"ex : ABC123XY",
                  en:"e.g. ABC123XY",
                  es:"ej: ABC123XY",
                  de:"z.B. ABC123XY",
                  pt:"ex: ABC123XY",
                  ar:"مثال: ABC123XY" },
  "ref.detected":{ fr:"Code de parrainage détecté",
                  en:"Referral code detected",
                  es:"Código de referido detectado",
                  de:"Empfehlungscode erkannt",
                  pt:"Código de indicação detectado",
                  ar:"تم اكتشاف رمز الإحالة" },
  /* forgot */
  "forgot":          { fr:"Mot de passe oublié ?",                     en:"Forgot password?",                     es:"¿Olvidaste tu contraseña?",             de:"Passwort vergessen?",                   pt:"Esqueceu a senha?",                     ar:"نسيت كلمة المرور؟"                 },
  /* checkboxes */
  "cgu.text":        { fr:<>J'accepte les <a onClick={e=>e.preventDefault()}>conditions générales d'utilisation</a> et la <a onClick={e=>e.preventDefault()}>politique de confidentialité</a>.</>,
                       en:<>I accept the <a onClick={e=>e.preventDefault()}>terms of use</a> and <a onClick={e=>e.preventDefault()}>privacy policy</a>.</>,
                       es:<>Acepto los <a onClick={e=>e.preventDefault()}>términos de uso</a> y la <a onClick={e=>e.preventDefault()}>política de privacidad</a>.</>,
                       de:<>Ich akzeptiere die <a onClick={e=>e.preventDefault()}>Nutzungsbedingungen</a> und <a onClick={e=>e.preventDefault()}>Datenschutzrichtlinie</a>.</>,
                       pt:<>Aceito os <a onClick={e=>e.preventDefault()}>termos de uso</a> e a <a onClick={e=>e.preventDefault()}>política de privacidade</a>.</>,
                       ar:<>أقبل <a onClick={e=>e.preventDefault()}>شروط الاستخدام</a> و<a onClick={e=>e.preventDefault()}>سياسة الخصوصية</a>.</> },
  "news.text":       { fr:"Je souhaite recevoir les actualités EVP et avantages exclusifs par email.",
                       en:"I want to receive EVP news and exclusive benefits by email.",
                       es:"Quiero recibir noticias EVP y beneficios exclusivos por email.",
                       de:"Ich möchte EVP-Neuigkeiten und exklusive Vorteile per E-Mail erhalten.",
                       pt:"Quero receber novidades EVP e benefícios exclusivos por email.",
                       ar:"أريد تلقي أخبار EVP والمزايا الحصرية عبر البريد الإلكتروني." },
  /* EVP bonus */
  "evp.bonus":       { fr:<>🎁 <strong>100 EVP offerts</strong> dès l'inscription — commencez à trader immédiatement, aucun dépôt requis.</>,
                       en:<>🎁 <strong>100 EVP offered</strong> upon registration — start trading immediately, no deposit required.</>,
                       es:<>🎁 <strong>100 EVP gratis</strong> al registrarte — empieza a operar de inmediato, sin depósito.</>,
                       de:<>🎁 <strong>100 EVP geschenkt</strong> bei Registrierung — sofort handeln, keine Einzahlung nötig.</>,
                       pt:<>🎁 <strong>100 EVP grátis</strong> ao se cadastrar — comece a negociar imediatamente, sem depósito.</>,
                       ar:<>🎁 <strong>100 EVP مجاناً</strong> عند التسجيل — ابدأ التداول فوراً، لا حاجة لإيداع.</> },
  /* submit */
  "btn.login":       { fr:"Se connecter",                              en:"Log in",                               es:"Iniciar sesión",                        de:"Anmelden",                              pt:"Entrar",                                ar:"تسجيل الدخول"                      },
  "btn.signup":      { fr:"Créer mon compte →",                        en:"Create my account →",                  es:"Crear mi cuenta →",                     de:"Mein Konto erstellen →",                pt:"Criar minha conta →",                   ar:"إنشاء حسابي →"                     },
  "btn.loading.login":{ fr:"Connexion en cours…",                      en:"Logging in…",                          es:"Iniciando sesión…",                     de:"Anmelden…",                             pt:"Entrando…",                             ar:"جارٍ تسجيل الدخول…"               },
  "btn.loading.signup":{ fr:"Création du compte…",                     en:"Creating account…",                    es:"Creando cuenta…",                       de:"Konto wird erstellt…",                  pt:"Criando conta…",                        ar:"جارٍ إنشاء الحساب…"               },
  /* errors */
  "err.email.req":   { fr:"L'email est requis.",                       en:"Email is required.",                   es:"El correo es requerido.",               de:"E-Mail ist erforderlich.",              pt:"E-mail é obrigatório.",                 ar:"البريد الإلكتروني مطلوب."          },
  "err.email.inv":   { fr:"Adresse email invalide.",                   en:"Invalid email address.",               es:"Dirección de correo inválida.",         de:"Ungültige E-Mail-Adresse.",             pt:"Endereço de e-mail inválido.",          ar:"عنوان بريد إلكتروني غير صالح."    },
  "err.pwd.req":     { fr:"Le mot de passe est requis.",               en:"Password is required.",                es:"La contraseña es requerida.",           de:"Passwort ist erforderlich.",            pt:"Senha é obrigatória.",                  ar:"كلمة المرور مطلوبة."               },
  "err.pwd.short":   { fr:"Minimum 8 caractères.",                     en:"Minimum 8 characters.",                es:"Mínimo 8 caracteres.",                  de:"Mindestens 8 Zeichen.",                 pt:"Mínimo 8 caracteres.",                  ar:"8 أحرف على الأقل."                },
  "err.cgu":         { fr:"Vous devez accepter les CGU.",              en:"You must accept the terms.",           es:"Debes aceptar los términos.",           de:"Sie müssen die AGB akzeptieren.",        pt:"Você deve aceitar os termos.",           ar:"يجب قبول الشروط."                  },
  "err.captcha":     { fr:"Veuillez compléter la vérification.",       en:"Please complete the verification.",    es:"Por favor completa la verificación.",   de:"Bitte Überprüfung abschließen.",        pt:"Por favor complete a verificação.",     ar:"يرجى إتمام التحقق."               },
  "err.server":      { fr:"Impossible de contacter le serveur.",       en:"Could not reach the server.",          es:"No se pudo contactar el servidor.",     de:"Server nicht erreichbar.",              pt:"Não foi possível contatar o servidor.", ar:"تعذر الوصول إلى الخادم."          },
  "err.google":      { fr:"La connexion Google n'est pas configurée.", en:"Google login is not configured.",      es:"El inicio de sesión con Google no está configurado.", de:"Google-Anmeldung nicht konfiguriert.", pt:"Login com Google não configurado.",     ar:"تسجيل الدخول بـ Google غير مهيأ." },
  /* verify OTP */
  "v.title":         { fr:"Vérifiez votre boîte mail",                 en:"Check your inbox",                     es:"Revisa tu correo",                      de:"E-Mail-Postfach prüfen",                pt:"Verifique sua caixa de entrada",        ar:"تحقق من بريدك الوارد"              },
  "v.desc":          { fr:"Nous avons envoyé un code de vérification à", en:"We sent a verification code to",    es:"Enviamos un código de verificación a",  de:"Wir haben einen Code gesendet an",      pt:"Enviamos um código de verificação para",ar:"أرسلنا رمز تحقق إلى"              },
   "v.s1":            { fr:"Ouvrez votre boîte mail et repérez l'email d'EVPTrading.", en:"Open your inbox and find the EVPTrading email.", es:"Abre tu correo y busca el email de EVPTrading.", de:"Öffnen Sie Ihr Postfach und finden Sie die EVPTrading-E-Mail.", pt:"Abra sua caixa de entrada e encontre o email da EVPTrading.", ar:"افتح بريدك الوارد وابحث عن بريد EVPTrading." },
  "v.s2":            { fr:"Copiez le code à 6 chiffres de l'email.", en:"Copy the 6-digit code from the email.", es:"Copia el código de 6 dígitos del email.", de:"Kopieren Sie den 6-stelligen Code aus der E-Mail.", pt:"Copie o código de 6 dígitos do email.", ar:"انسخ الرمز المكوّن من 6 أرقام من البريد." },
  "v.s3":            { fr:"Collez-le ci-dessous pour activer votre compte.", en:"Paste it below to activate your account.", es:"Pégalo abajo para activar tu cuenta.", de:"Fügen Sie ihn unten ein, um Ihr Konto zu aktivieren.", pt:"Cole abaixo para ativar sua conta.", ar:"الصقه أدناه لتفعيل حسابك." },
  "v.label":         { fr:"Code de vérification",                      en:"Verification code",                    es:"Código de verificación",                de:"Verifizierungscode",                    pt:"Código de verificação",                 ar:"رمز التحقق"                        },
  "v.ph":            { fr:"ex : 482 751",                              en:"e.g. 482 751",                         es:"ej: 482 751",                            de:"z.B. 482 751",                          pt:"ex: 482 751",                           ar:"مثال: 482 751"                     },
  "v.err.digits":    { fr:"Le code doit contenir 6 chiffres.",         en:"Code must be 6 digits.",               es:"El código debe tener 6 dígitos.",       de:"Code muss 6 Ziffern haben.",            pt:"Código deve ter 6 dígitos.",            ar:"يجب أن يتكون الرمز من 6 أرقام."  },
  "v.err.fallback":  { fr:"Code incorrect ou expiré.",                 en:"Incorrect or expired code.",           es:"Código incorrecto o expirado.",         de:"Falscher oder abgelaufener Code.",      pt:"Código incorreto ou expirado.",         ar:"رمز غير صحيح أو منتهي الصلاحية."  },
  "v.btn":           { fr:"Activer mon compte →",                      en:"Activate my account →",                es:"Activar mi cuenta →",                   de:"Mein Konto aktivieren →",               pt:"Ativar minha conta →",                  ar:"تفعيل حسابي →"                    },
  "v.checking":      { fr:"Vérification…",                             en:"Verifying…",                           es:"Verificando…",                          de:"Überprüfung…",                          pt:"Verificando…",                          ar:"جارٍ التحقق…"                     },
  "v.no.recv":       { fr:"Pas reçu ?",                                en:"Didn't receive it?",                   es:"¿No lo recibiste?",                     de:"Nicht erhalten?",                       pt:"Não recebeu?",                          ar:"لم تستلمه؟"                        },
  "v.resend":        { fr:"Renvoyer le code",                          en:"Resend code",                          es:"Reenviar código",                       de:"Code erneut senden",                    pt:"Reenviar código",                       ar:"إعادة إرسال الرمز"                },
  "v.resending":     { fr:"Envoi…",                                    en:"Sending…",                             es:"Enviando…",                             de:"Senden…",                               pt:"Enviando…",                             ar:"جارٍ الإرسال…"                   },
  "v.resend.in":     { fr:"Renvoyer dans",                             en:"Resend in",                            es:"Reenviar en",                           de:"Erneut senden in",                      pt:"Reenviar em",                           ar:"إعادة الإرسال خلال"               },
  "v.back":          { fr:"← Corriger l'adresse email",               en:"← Edit email address",                 es:"← Editar el correo",                    de:"← E-Mail-Adresse bearbeiten",           pt:"← Editar endereço de email",            ar:"← تعديل عنوان البريد"             },
  /* forgot email step */
  "fe.title":        { fr:"Mot de passe oublié ?",                     en:"Forgot your password?",                es:"¿Olvidaste tu contraseña?",             de:"Passwort vergessen?",                   pt:"Esqueceu sua senha?",                   ar:"نسيت كلمة المرور؟"                },
  "fe.desc":         { fr:"Entrez votre adresse email pour recevoir un code de réinitialisation.", en:"Enter your email to receive a reset code.", es:"Ingresa tu correo para recibir un código de restablecimiento.", de:"Geben Sie Ihre E-Mail ein, um einen Reset-Code zu erhalten.", pt:"Insira seu email para receber um código de redefinição.", ar:"أدخل بريدك الإلكتروني لتلقي رمز إعادة التعيين." },
  "fe.btn":          { fr:"Envoyer le code →",                         en:"Send code →",                          es:"Enviar código →",                       de:"Code senden →",                         pt:"Enviar código →",                       ar:"إرسال الرمز →"                    },
  "fe.sending":      { fr:"Envoi en cours…",                           en:"Sending…",                             es:"Enviando…",                             de:"Senden…",                               pt:"Enviando…",                             ar:"جارٍ الإرسال…"                   },
  "fe.back":         { fr:"← Retour à la connexion",                   en:"← Back to login",                      es:"← Volver al inicio de sesión",          de:"← Zurück zur Anmeldung",                pt:"← Voltar ao login",                     ar:"← العودة لتسجيل الدخول"           },
  /* forgot code step */
  "fc.title":        { fr:"Entrez votre code",                         en:"Enter your code",                      es:"Ingresa tu código",                     de:"Code eingeben",                         pt:"Insira seu código",                     ar:"أدخل رمزك"                        },
  "fc.desc":         { fr:"Code envoyé à",                             en:"Code sent to",                         es:"Código enviado a",                      de:"Code gesendet an",                      pt:"Código enviado para",                   ar:"تم إرسال الرمز إلى"               },
  "fc.s1":           { fr:"Ouvrez votre boîte mail et repérez l'email d'EVPTrading.", en:"Open your inbox and find the EVPTrading email.", es:"Abre tu correo y busca el email de EVPTrading.", de:"Postfach öffnen und EVPTrading-E-Mail finden.", pt:"Abra o email da EVPTrading na sua caixa de entrada.", ar:"افتح بريدك وابحث عن بريد EVPTrading." },
  "fc.s2":           { fr:"Copiez le code rouge à 6 chiffres.",        en:"Copy the 6-digit red code.",           es:"Copia el código rojo de 6 dígitos.",    de:"Kopieren Sie den roten 6-stelligen Code.", pt:"Copie o código vermelho de 6 dígitos.", ar:"انسخ الرمز الأحمر المكوّن من 6 أرقام." },
  "fc.s3":           { fr:"Collez-le ci-dessous.",                     en:"Paste it below.",                      es:"Pégalo abajo.",                         de:"Fügen Sie ihn unten ein.",               pt:"Cole-o abaixo.",                        ar:"الصقه أدناه."                     },
  "fc.label":        { fr:"Code de réinitialisation",                  en:"Reset code",                          es:"Código de restablecimiento",            de:"Reset-Code",                            pt:"Código de redefinição",                 ar:"رمز إعادة التعيين"                },
  "fc.btn":          { fr:"Valider le code →",                         en:"Validate code →",                      es:"Validar código →",                      de:"Code bestätigen →",                     pt:"Validar código →",                      ar:"تأكيد الرمز →"                    },
  "fc.checking":     { fr:"Vérification…",                             en:"Verifying…",                           es:"Verificando…",                          de:"Überprüfung…",                          pt:"Verificando…",                          ar:"جارٍ التحقق…"                     },
  "fc.back":         { fr:"← Modifier l'adresse email",               en:"← Change email address",               es:"← Cambiar el correo",                   de:"← E-Mail ändern",                       pt:"← Alterar endereço de email",           ar:"← تغيير عنوان البريد"             },
  /* forgot new pwd */
  "fp.title":        { fr:"Nouveau mot de passe",                      en:"New password",                        es:"Nueva contraseña",                      de:"Neues Passwort",                        pt:"Nova senha",                            ar:"كلمة مرور جديدة"                  },
  "fp.desc":         { fr:"Choisissez un mot de passe sécurisé pour votre compte.", en:"Choose a secure password for your account.", es:"Elige una contraseña segura para tu cuenta.", de:"Wählen Sie ein sicheres Passwort.", pt:"Escolha uma senha segura para sua conta.", ar:"اختر كلمة مرور آمنة لحسابك." },
  "fp.new":          { fr:"Nouveau mot de passe",                      en:"New password",                        es:"Nueva contraseña",                      de:"Neues Passwort",                        pt:"Nova senha",                            ar:"كلمة المرور الجديدة"               },
  "fp.new.ph":       { fr:"Minimum 8 caractères",                      en:"Minimum 8 characters",                es:"Mínimo 8 caracteres",                   de:"Mindestens 8 Zeichen",                  pt:"Mínimo 8 caracteres",                  ar:"8 أحرف على الأقل"                 },
  "fp.confirm":      { fr:"Confirmer le mot de passe",                 en:"Confirm password",                    es:"Confirmar contraseña",                  de:"Passwort bestätigen",                   pt:"Confirmar senha",                      ar:"تأكيد كلمة المرور"                },
  "fp.confirm.ph":   { fr:"Répétez le mot de passe",                   en:"Repeat password",                     es:"Repite la contraseña",                  de:"Passwort wiederholen",                  pt:"Repita a senha",                       ar:"أعد كلمة المرور"                  },
  "fp.match.no":     { fr:"Les mots de passe ne correspondent pas.",   en:"Passwords don't match.",              es:"Las contraseñas no coinciden.",         de:"Passwörter stimmen nicht überein.",     pt:"As senhas não coincidem.",              ar:"كلمتا المرور غير متطابقتين."      },
  "fp.match.yes":    { fr:"✓ Les mots de passe correspondent.",        en:"✓ Passwords match.",                  es:"✓ Las contraseñas coinciden.",          de:"✓ Passwörter stimmen überein.",         pt:"✓ As senhas coincidem.",               ar:"✓ كلمتا المرور متطابقتان."         },
  "fp.btn":          { fr:"Enregistrer le nouveau mot de passe ✓",     en:"Save new password ✓",                 es:"Guardar nueva contraseña ✓",            de:"Neues Passwort speichern ✓",            pt:"Salvar nova senha ✓",                  ar:"حفظ كلمة المرور الجديدة ✓"        },
  "fp.saving":       { fr:"Réinitialisation…",                         en:"Resetting…",                          es:"Restableciendo…",                       de:"Zurücksetzen…",                         pt:"Redefinindo…",                          ar:"جارٍ إعادة التعيين…"              },
  "fp.err.short":    { fr:"Minimum 8 caractères.",                     en:"Minimum 8 characters.",               es:"Mínimo 8 caracteres.",                  de:"Mindestens 8 Zeichen.",                 pt:"Mínimo 8 caracteres.",                 ar:"8 أحرف على الأقل."                },
  "fp.err.match":    { fr:"Les mots de passe ne correspondent pas.",   en:"Passwords don't match.",              es:"Las contraseñas no coinciden.",         de:"Passwörter stimmen nicht überein.",     pt:"As senhas não coincidem.",              ar:"كلمتا المرور غير متطابقتين."      },
  "fp.err.fallback": { fr:"Erreur lors de la réinitialisation.",       en:"Error during password reset.",        es:"Error al restablecer la contraseña.",   de:"Fehler beim Zurücksetzen des Passworts.", pt:"Erro ao redefinir a senha.",          ar:"خطأ أثناء إعادة تعيين كلمة المرور."},
  /* lang bar */
  "lang.label":      { fr:"Langue :",                                  en:"Language:",                           es:"Idioma:",                               de:"Sprache:",                              pt:"Idioma:",                               ar:":اللغة"                            },
};

function useT(lang) {
  return (key) => {
    const v = TX[key];
    if (!v) return key;
    return v[lang] ?? v.fr ?? key;
  };
}

/* ─── API error translation ─────────────────────────────────────────────── */
const API_ERROR_MAP = {
  /* Messages génériques DRF (rarement déclenchés ici, gardés par sécurité) */
  "No active account found with the given credentials": { fr:"Email ou mot de passe incorrect.",en:"Email or password is incorrect.",es:"Email o contraseña incorrectos.",de:"E-Mail oder Passwort falsch.",pt:"Email ou senha incorretos.",ar:"البريد الإلكتروني أو كلمة المرور غير صحيحة." },
  "This field may not be blank.": { fr:"Ce champ est requis.",en:"This field is required.",es:"Este campo es requerido.",de:"Dieses Feld ist erforderlich.",pt:"Este campo é obrigatório.",ar:"هذا الحقل مطلوب." },
  "Enter a valid email address.": { fr:"Adresse email invalide.",en:"Invalid email address.",es:"Dirección de email inválida.",de:"Ungültige E-Mail.",pt:"Email inválido.",ar:"عنوان بريد غير صالح." },
  "This password is too short.": { fr:"Mot de passe trop court (minimum 8 caractères).",en:"Password too short (minimum 8 characters).",es:"Contraseña muy corta (mínimo 8 caracteres).",de:"Passwort zu kurz (min. 8 Zeichen).",pt:"Senha muito curta (mínimo 8 caracteres).",ar:"كلمة المرور قصيرة جداً (8 أحرف على الأقل)." },
  "user with this email address already exists.": { fr:"Un compte existe déjà avec cet email.",en:"An account already exists with this email.",es:"Ya existe una cuenta con este correo.",de:"Ein Konto mit dieser E-Mail existiert bereits.",pt:"Já existe uma conta com este email.",ar:"حساب موجود بالفعل بهذا البريد." },

  /* Messages réels renvoyés par le backend (core_apps/evenement/views.py) —
     tous en français côté API, donc matchés ici sur leur texte français
     exact pour être re-traduits dans la langue de l'utilisateur. */
  "Email et mot de passe requis.": { fr:"Email et mot de passe requis.",en:"Email and password are required.",es:"Email y contraseña son requeridos.",de:"E-Mail und Passwort sind erforderlich.",pt:"Email e senha são obrigatórios.",ar:"البريد الإلكتروني وكلمة المرور مطلوبان." },
  "Un compte existe déjà avec cet email.": { fr:"Un compte existe déjà avec cet email.",en:"An account already exists with this email.",es:"Ya existe una cuenta con este correo.",de:"Ein Konto mit dieser E-Mail existiert bereits.",pt:"Já existe uma conta com este email.",ar:"يوجد حساب بالفعل بهذا البريد الإلكتروني." },
  "Mot de passe trop court (minimum 8 caractères).": { fr:"Mot de passe trop court (minimum 8 caractères).",en:"Password too short (minimum 8 characters).",es:"Contraseña muy corta (mínimo 8 caracteres).",de:"Passwort zu kurz (min. 8 Zeichen).",pt:"Senha muito curta (mínimo 8 caracteres).",ar:"كلمة المرور قصيرة جداً (8 أحرف على الأقل)." },
  "Vérification de sécurité manquante.": { fr:"Vérification de sécurité manquante.",en:"Security verification missing.",es:"Falta la verificación de seguridad.",de:"Sicherheitsüberprüfung fehlt.",pt:"Verificação de segurança ausente.",ar:"التحقق الأمني مفقود." },
  "Le CAPTCHA a expiré. Veuillez le refaire.": { fr:"Le CAPTCHA a expiré. Veuillez le refaire.",en:"The CAPTCHA has expired. Please try again.",es:"El CAPTCHA ha expirado. Vuelve a intentarlo.",de:"Das CAPTCHA ist abgelaufen. Bitte erneut versuchen.",pt:"O CAPTCHA expirou. Tente novamente.",ar:"انتهت صلاحية التحقق. يرجى إعادة المحاولة." },
  "Vérification de sécurité échouée. Réessayez.": { fr:"Vérification de sécurité échouée. Réessayez.",en:"Security verification failed. Please try again.",es:"La verificación de seguridad falló. Vuelve a intentarlo.",de:"Sicherheitsüberprüfung fehlgeschlagen. Bitte erneut versuchen.",pt:"A verificação de segurança falhou. Tente novamente.",ar:"فشل التحقق الأمني. يرجى إعادة المحاولة." },
  "Email et code requis.": { fr:"Email et code requis.",en:"Email and code are required.",es:"Email y código son requeridos.",de:"E-Mail und Code sind erforderlich.",pt:"Email e código são obrigatórios.",ar:"البريد الإلكتروني والرمز مطلوبان." },
  "Code expiré. Demandez un nouveau code.": { fr:"Code expiré. Demandez un nouveau code.",en:"Code expired. Please request a new one.",es:"Código expirado. Solicita uno nuevo.",de:"Code abgelaufen. Bitte fordern Sie einen neuen an.",pt:"Código expirado. Solicite um novo.",ar:"انتهت صلاحية الرمز. اطلب رمزاً جديداً." },
  "Code incorrect.": { fr:"Code incorrect.",en:"Incorrect code.",es:"Código incorrecto.",de:"Falscher Code.",pt:"Código incorreto.",ar:"رمز غير صحيح." },
  "Compte introuvable.": { fr:"Compte introuvable.",en:"Account not found.",es:"Cuenta no encontrada.",de:"Konto nicht gefunden.",pt:"Conta não encontrada.",ar:"الحساب غير موجود." },
  "Email requis.": { fr:"Email requis.",en:"Email is required.",es:"Email requerido.",de:"E-Mail ist erforderlich.",pt:"Email obrigatório.",ar:"البريد الإلكتروني مطلوب." },
  "Champs manquants.": { fr:"Champs manquants.",en:"Missing fields.",es:"Campos faltantes.",de:"Fehlende Felder.",pt:"Campos ausentes.",ar:"حقول ناقصة." },
  "Session expirée. Recommencez la procédure.": { fr:"Session expirée. Recommencez la procédure.",en:"Session expired. Please start over.",es:"Sesión expirada. Vuelve a empezar el proceso.",de:"Sitzung abgelaufen. Bitte Vorgang neu starten.",pt:"Sessão expirada. Reinicie o processo.",ar:"انتهت الجلسة. يرجى إعادة العملية." },
};
function translateApiError(msg, lang) {
  if (!msg) return msg;
  for (const [en, translations] of Object.entries(API_ERROR_MAP)) {
    if (msg.toLowerCase().includes(en.toLowerCase())) return translations[lang] ?? translations.fr;
  }
  return msg;
}

/* ═══════════════════════════════════════════════════════════════════════════
   VERIFY STEP
═══════════════════════════════════════════════════════════════════════════ */
function VerifyStep({ email, onSuccess, onBack, lang }) {
  const t = useT(lang);
  const [code, setCode]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    const trimmed = code.trim();
    if (trimmed.length !== 6) { setError(t("v.err.digits")); return; }
    setLoading(true); setError("");
    try {
      const res = await verifyEmail({ email, code: trimmed });
      localStorage.setItem("access_token",  res.data.access);
      localStorage.setItem("refresh_token", res.data.refresh);
      localStorage.setItem("username",      res.data.username);
      localStorage.setItem("is_staff",      res.data.is_staff  ? "true" : "false");
      localStorage.setItem("is_admin",      res.data.is_admin  ? "true" : "false");
      localStorage.setItem("role",          res.data.role || "viewer");
      onSuccess({ username:res.data.username, isAdmin:res.data.is_admin, isStaff:res.data.is_staff, role:res.data.role||"viewer" });
    } catch (err) {
      setError(translateApiError(err?.response?.data?.detail || t("v.err.fallback"), lang));
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true); setError("");
    try { await resendCode({ email }); setCooldown(60); }
    catch { setError(t("err.server")); }
    finally { setResending(false); }
  };

  return (
    <VerifyWrap>
      <VerifyIcon>📬</VerifyIcon>
      <div style={{textAlign:"center"}}>
        <VerifyTitle>{t("v.title")}</VerifyTitle>
        <VerifyDesc style={{marginTop:".4rem"}}>{t("v.desc")}</VerifyDesc>
      </div>
      <EmailChip>{email}</EmailChip>
      <StepList>
        <StepItem><StepNum>1</StepNum>{t("v.s1")}</StepItem>
        <StepItem><StepNum>2</StepNum>{t("v.s2")}</StepItem>
        <StepItem><StepNum>3</StepNum>{t("v.s3")}</StepItem>
      </StepList>
      <Field>
        <FieldLabel>{t("v.label")}</FieldLabel>
        <FieldInput
          placeholder={t("v.ph")} value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/g,"").slice(0,6)); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handleVerify()}
          inputMode="numeric" maxLength={6}
          style={{fontFamily:"'DM Mono',monospace",letterSpacing:".2em",fontSize:"1rem",textAlign:"center"}}
          $err={!!error} autoFocus
        />
        {error && <span style={{fontSize:".7rem",color:"#ff3b5c"}}>⚠ {error}</span>}
      </Field>
      <SubmitBtn onClick={handleVerify} disabled={code.trim().length !== 6 || loading}>
        {loading ? t("v.checking") : t("v.btn")}
      </SubmitBtn>
      <div style={{textAlign:"center",fontSize:".72rem",color:"#5a6a7a"}}>
        {t("v.no.recv")}{" "}
        {cooldown > 0
          ? <CountDown>{t("v.resend.in")} {cooldown}s</CountDown>
          : <ResendBtn onClick={handleResend} disabled={resending}>{resending ? t("v.resending") : t("v.resend")}</ResendBtn>
        }
      </div>
      <BackLink onClick={onBack}>{t("v.back")}</BackLink>
    </VerifyWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FORGOT — STEP 1 : email
═══════════════════════════════════════════════════════════════════════════ */
function ForgotEmailStep({ onCodeSent, onBack, lang }) {
  const t = useT(lang);
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handle = async () => {
    if (!email.includes("@")) { setError(t("err.email.inv")); return; }
    setLoading(true); setError("");
    try { await forgotPassword({ email: email.trim().toLowerCase() }); onCodeSent(email.trim().toLowerCase()); }
    catch { setError(t("err.server")); }
    finally { setLoading(false); }
  };

  return (
    <VerifyWrap>
      <VerifyIcon>🔑</VerifyIcon>
      <div style={{textAlign:"center"}}>
        <VerifyTitle>{t("fe.title")}</VerifyTitle>
        <VerifyDesc style={{marginTop:".4rem"}}>{t("fe.desc")}</VerifyDesc>
      </div>
      <Field>
        <FieldLabel>{t("f.email")}</FieldLabel>
        <FieldInput
          type="email" placeholder={t("f.email.ph")} value={email} $err={!!error}
          onChange={e => { setEmail(e.target.value.toLowerCase()); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handle()} autoFocus
        />
        {error && <span style={{fontSize:".7rem",color:"#ff3b5c"}}>⚠ {error}</span>}
      </Field>
      <SubmitBtn onClick={handle} disabled={!email.includes("@") || loading}>
        {loading ? t("fe.sending") : t("fe.btn")}
      </SubmitBtn>
      <BackLink onClick={onBack}>{t("fe.back")}</BackLink>
    </VerifyWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FORGOT — STEP 2 : code
═══════════════════════════════════════════════════════════════════════════ */
function ForgotCodeStep({ email, onTokenReceived, onBack, lang }) {
  const t = useT(lang);
  const [code, setCode]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handle = async () => {
    if (code.trim().length !== 6) { setError(t("v.err.digits")); return; }
    setLoading(true); setError("");
    try {
      const res = await resetPasswordVerify({ email, code: code.trim() });
      onTokenReceived(res.data.reset_token);
    } catch (err) {
      setError(translateApiError(err?.response?.data?.detail || t("v.err.fallback"), lang));
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try { await forgotPassword({ email }); setCooldown(60); }
    catch { setError(t("err.server")); }
    finally { setResending(false); }
  };

  return (
    <VerifyWrap>
      <VerifyIcon>📬</VerifyIcon>
      <div style={{textAlign:"center"}}>
        <VerifyTitle>{t("fc.title")}</VerifyTitle>
        <VerifyDesc style={{marginTop:".4rem"}}>{t("fc.desc")}</VerifyDesc>
      </div>
      <EmailChip>{email}</EmailChip>
      <StepList>
        <StepItem><StepNum>1</StepNum>{t("fc.s1")}</StepItem>
        <StepItem><StepNum>2</StepNum>{t("fc.s2")}</StepItem>
        <StepItem><StepNum>3</StepNum>{t("fc.s3")}</StepItem>
      </StepList>
      <Field>
        <FieldLabel>{t("fc.label")}</FieldLabel>
        <FieldInput
          placeholder={t("v.ph")} value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/g,"").slice(0,6)); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handle()}
          inputMode="numeric" maxLength={6}
          style={{fontFamily:"'DM Mono',monospace",letterSpacing:".2em",fontSize:"1rem",textAlign:"center"}}
          $err={!!error} autoFocus
        />
        {error && <span style={{fontSize:".7rem",color:"#ff3b5c"}}>⚠ {error}</span>}
      </Field>
      <SubmitBtn onClick={handle} disabled={code.trim().length !== 6 || loading}>
        {loading ? t("fc.checking") : t("fc.btn")}
      </SubmitBtn>
      <div style={{textAlign:"center",fontSize:".72rem",color:"#5a6a7a"}}>
        {t("v.no.recv")}{" "}
        {cooldown > 0
          ? <CountDown>{t("v.resend.in")} {cooldown}s</CountDown>
          : <ResendBtn onClick={handleResend} disabled={resending}>{resending ? t("v.resending") : t("v.resend")}</ResendBtn>
        }
      </div>
      <BackLink onClick={onBack}>{t("fc.back")}</BackLink>
    </VerifyWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FORGOT — STEP 3 : new password
═══════════════════════════════════════════════════════════════════════════ */
function ForgotNewPwdStep({ email, resetToken, onSuccess, lang }) {
  const t = useT(lang);
  const [pwd, setPwd]         = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const strength = getStrength(pwd);
  const match    = pwd && confirm && pwd === confirm;
  const mismatch = confirm && pwd !== confirm;
  const STRENGTH = ["", t("str.1"), t("str.2"), t("str.3"), t("str.4")];

  const handle = async () => {
    if (pwd.length < 8) { setError(t("fp.err.short")); return; }
    if (!match)         { setError(t("fp.err.match")); return; }
    setLoading(true); setError("");
    try {
      const res = await resetPasswordConfirm({ email, reset_token: resetToken, new_password: pwd });
      localStorage.setItem("access_token",  res.data.access);
      localStorage.setItem("refresh_token", res.data.refresh);
      localStorage.setItem("username",      res.data.username);
      localStorage.setItem("is_staff",      res.data.is_staff  ? "true" : "false");
      localStorage.setItem("is_admin",      res.data.is_admin  ? "true" : "false");
      localStorage.setItem("role",          res.data.role || "viewer");
      onSuccess({ username:res.data.username, isAdmin:res.data.is_admin, isStaff:res.data.is_staff, role:res.data.role||"viewer" });
    } catch (err) {
      setError(translateApiError(err?.response?.data?.detail || t("fp.err.fallback"), lang));
    } finally { setLoading(false); }
  };

  return (
    <VerifyWrap>
      <VerifyIcon>🔒</VerifyIcon>
      <div style={{textAlign:"center"}}>
        <VerifyTitle>{t("fp.title")}</VerifyTitle>
        <VerifyDesc style={{marginTop:".4rem"}}>{t("fp.desc")}</VerifyDesc>
      </div>
      <Form>
        <Field>
          <FieldLabel>{t("fp.new")}</FieldLabel>
          <PasswordWrapper>
            <PasswordInput
              type={showPwd?"text":"password"} placeholder={t("fp.new.ph")}
              value={pwd} onChange={e => { setPwd(e.target.value); setError(""); }}
              autoFocus autoComplete="new-password"
            />
            <EyeBtn type="button" tabIndex={-1} onClick={() => setShowPwd(v=>!v)} title={showPwd?t("f.pwd.hide"):t("f.pwd.show")}>
              {showPwd?"🙈":"👁"}
            </EyeBtn>
          </PasswordWrapper>
          {pwd && (
            <>
              <PasswordStrengthBar $l={strength}/>
              <span style={{fontSize:".65rem",color:strength>=3?"#00e676":strength>=2?"#ffc107":"#ff3b5c"}}>{STRENGTH[strength]}</span>
            </>
          )}
        </Field>
        <Field>
          <FieldLabel>{t("fp.confirm")}</FieldLabel>
          <PasswordInput
            type="password" placeholder={t("fp.confirm.ph")}
            value={confirm} onChange={e => { setConfirm(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handle()}
            $err={!!mismatch} autoComplete="new-password"
          />
          {mismatch && <span style={{fontSize:".7rem",color:"#ff3b5c"}}>⚠ {t("fp.match.no")}</span>}
          {match    && <span style={{fontSize:".7rem",color:"#00e676"}}>{t("fp.match.yes")}</span>}
        </Field>
        {error && <span style={{fontSize:".75rem",color:"#ff3b5c",background:"#ff3b5c11",border:"1px solid #ff3b5c33",borderRadius:"8px",padding:".6rem .85rem"}}>⚠ {error}</span>}
        <SubmitBtn onClick={handle} disabled={!match || pwd.length < 8 || loading}>
          {loading ? t("fp.saving") : t("fp.btn")}
        </SubmitBtn>
      </Form>
    </VerifyWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN AUTH MODAL
═══════════════════════════════════════════════════════════════════════════ */
export default function AuthModal({ initialMode = "login", onClose, onAuth }) {
  const { lang, setLang } = useI18n();
  const t = useT(lang);
  const dir = lang === "ar" ? "rtl" : "ltr";

  const [mode,           setMode]           = useState(initialMode);
  const [step,           setStep]           = useState("form"); // form|verify|forgot-email|forgot-code|forgot-newpwd
  const [pendingEmail,   setPendingEmail]   = useState("");
  const [resetToken,     setResetToken]     = useState("");
  const [email,          setEmail]          = useState("");
  const [password,       setPassword]       = useState("");
  const [loading,        setLoading]        = useState(false);
  const [googleLoad,     setGoogleLoad]     = useState(false);
  const [showPwd,        setShowPwd]        = useState(false);
  const [acceptCGU,      setAcceptCGU]      = useState(false);
  const [acceptNews,     setAcceptNews]     = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [turnstileErr,   setTurnstileErr]   = useState("");
  const STRENGTH_LABELS = ["", t("str.1"), t("str.2"), t("str.3"), t("str.4")];
  const strength = getStrength(password);

  const { errors, globalError, setFieldError, setGlobalError, clearAll } = useFormErrors();

const [referralCode, setReferralCode] = useState("");
const [refDetected,  setRefDetected]  = useState(false);

// Au mount, charger le code stocké par App.jsx (si pas expiré)
useEffect(() => {
  const stored = localStorage.getItem("evenmt_referral_code");
  const expiry = parseInt(localStorage.getItem("evenmt_referral_expiry") || "0");
  if (stored && expiry > Date.now()) {
    setReferralCode(stored);
    setRefDetected(true);
  }
}, []);
  useEffect(() => { loadGoogleScript().catch(() => {}); }, []);
useEffect(() => { trackPageView(mode); }, [mode]);

  /* Google OAuth */
const handleGoogle = async () => {
  if (!GOOGLE_CLIENT_ID) { setGlobalError(t("err.google")); return; }
  setGoogleLoad(true); clearAll();
  try {
    const code = await loginWithGoogle();
    const API  = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
 
    // ── Récupérer le code de parrainage si présent ──
    const referralCode = localStorage.getItem("referral_code") || null;
 
    const res  = await fetch(`${API}/api/v1/account/google/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        referral_code: referralCode,   // ← envoyé seulement si présent
        language: lang,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || t("err.google"));
 
    localStorage.setItem("access_token",  data.access);
    localStorage.setItem("refresh_token", data.refresh);
    localStorage.setItem("username",      data.username);
    localStorage.setItem("is_staff",      data.is_staff  ? "true" : "false");
    localStorage.setItem("is_admin",      data.is_admin  ? "true" : "false");
    localStorage.setItem("role",          data.role || "viewer");
 
    // ── Si le compte vient d'être créé, nettoyer le code parrain ──
    // (il a été consommé, pas besoin de le garder)
    if (data.created) {
      localStorage.removeItem("referral_code");
      localStorage.removeItem("referral_code_at");
    }
 
    onAuth({       username:data.username,       isAdmin:data.is_admin,       isStaff:data.is_staff,       role:data.role||"viewer"     });
    onClose();
  } catch (e) {
    // Annulation volontaire (popup fermée / accès refusé) : on revient simplement au formulaire, sans erreur
    if (!["popup_closed", "popup_closed_by_user", "access_denied"].includes(e.message)) {
      setGlobalError(translateApiError(e.message, lang) || t("err.google"));
    }
  } finally { setGoogleLoad(false); }
};

  /* Email / password submit */
  const handleSubmit = async () => {
    let hasErr = false;
    if (!email.trim())                                 { setFieldError("email",    t("err.email.req")); hasErr = true; }
    else if (!email.includes("@"))                     { setFieldError("email",    t("err.email.inv")); hasErr = true; }
    if (!password)                                     { setFieldError("password", t("err.pwd.req"));   hasErr = true; }
    else if (mode === "signup" && password.length < 8) { setFieldError("password", t("err.pwd.short")); hasErr = true; }
    if (mode === "signup" && !acceptCGU)               { setFieldError("cgu",      t("err.cgu"));        hasErr = true; }
    if (hasErr) return;

    setLoading(true);
    try {
      if (mode === "signup") {
        const res = await signup({ email, password, accept_newsletter: acceptNews, referral_code: referralCode || undefined, cf_turnstile_token: turnstileToken, language: lang });
        if (res.data?.requires_verification) { setPendingEmail(email); setStep("verify");
            localStorage.removeItem("evenmt_referral_code");
  localStorage.removeItem("evenmt_referral_expiry");
   return; }
      }
      const res = await login({ email, password, cf_turnstile_token: turnstileToken });
      localStorage.setItem("access_token",  res.data.access);
      localStorage.setItem("refresh_token", res.data.refresh);
      localStorage.setItem("username",      res.data.username);
      localStorage.setItem("is_staff",      res.data.is_staff  ? "true" : "false");
      localStorage.setItem("is_admin",      res.data.is_admin  ? "true" : "false");
      localStorage.setItem("role",          res.data.role || "viewer");
      onAuth({ username:res.data.username, isAdmin:res.data.is_admin, isStaff:res.data.is_staff, role:res.data.role||"viewer" });
      onClose();
    } catch (e) {
      setTurnstileToken(""); setTurnstileReset(r => r + 1);
      const data = e.response?.data;
      if (!data) { setGlobalError(t("err.server")); return; }
      if (data.detail) { setGlobalError(translateApiError(data.detail, lang)); return; }
      if (typeof data === "object") {
        let hasField = false;
        Object.entries(data).forEach(([field, errs]) => {
          const msg = translateApiError(Array.isArray(errs) ? errs.join(" ") : String(errs), lang);
          if (field === "non_field_errors") setGlobalError(msg);
          else { setFieldError(field, msg); hasField = true; }
        });
      }
    } finally { setLoading(false); }
  };

  const handleKey = e => { if (e.key === "Enter") handleSubmit(); };

  /* Subtitle routing */
  const subtitle = {
    "verify":         t("sub.verify"),
    "forgot-email":   t("sub.forgot"),
    "forgot-code":    t("sub.forgot-code"),
    "forgot-newpwd":  t("sub.forgot-pwd"),
  }[step] ?? (mode === "login" ? t("sub.login") : t("sub.signup"));

  const sharedStepProps = { lang };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal dir={dir}>

        {/* ── Header ── */}
        <Head>
          <div>
            <ModalTitle>EVP <span>Trading</span></ModalTitle>
            <ModalSub>{subtitle}</ModalSub>
          </div>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </Head>

        {/* ── Step router ── */}
        {step === "verify" ? (
          <VerifyStep email={pendingEmail} onSuccess={userData => { onAuth(userData); onClose(); }} onBack={() => { setStep("form"); setPassword(""); }} {...sharedStepProps}/>
        ) : step === "forgot-email" ? (
          <ForgotEmailStep onCodeSent={em => { setPendingEmail(em); setStep("forgot-code"); }} onBack={() => setStep("form")} {...sharedStepProps}/>
        ) : step === "forgot-code" ? (
          <ForgotCodeStep email={pendingEmail} onTokenReceived={tok => { setResetToken(tok); setStep("forgot-newpwd"); }} onBack={() => setStep("forgot-email")} {...sharedStepProps}/>
        ) : step === "forgot-newpwd" ? (
          <ForgotNewPwdStep email={pendingEmail} resetToken={resetToken} onSuccess={userData => { onAuth(userData); onClose(); }} {...sharedStepProps}/>
        ) : (
          <>
            <TabRow>
              <Tab $active={mode==="login"}  onClick={()=>{ setMode("login");  clearAll(); setShowPwd(false); }}>{t("tab.login")}</Tab>
              <Tab $active={mode==="signup"} onClick={()=>{ setMode("signup"); clearAll(); setShowPwd(false); }}>{t("tab.signup")}</Tab>
            </TabRow>

            {/* EVP bonus banner — signup only */}
            {mode === "signup" && (
              <EVPBonusBanner>{t("evp.bonus")}</EVPBonusBanner>
            )}

            <GoogleBtn onClick={handleGoogle} disabled={loading||googleLoad}>
              {googleLoad
                ? <span style={{fontSize:".8rem"}}>{t("google.loading")}</span>
                : <><GoogleIcon/>{mode==="login" ? t("google.login") : t("google.signup")}</>
              }
            </GoogleBtn>

            <Separator>{t("or")}</Separator>

            <Form>
              <Field>
                <FieldLabel>{t("f.email")}</FieldLabel>
                <FieldInput
                  type="email" placeholder={t("f.email.ph")} value={email} $err={!!errors.email}
                  onChange={e => setEmail(e.target.value.toLowerCase())} onKeyDown={handleKey}
                  autoComplete="email"
                />
                <FieldError $show={!!errors.email}>{errors.email}</FieldError>
              </Field>

              <Field>
                <FieldLabel>{t("f.password")}</FieldLabel>
                <PasswordWrapper>
                  <PasswordInput
                    type={showPwd?"text":"password"} placeholder={t("f.password.ph")}
                    value={password} $err={!!errors.password}
                    onChange={e => setPassword(e.target.value)} onKeyDown={handleKey}
                    autoComplete={mode==="login"?"current-password":"new-password"}
                  />
                  <EyeBtn type="button" tabIndex={-1} onClick={() => setShowPwd(v=>!v)} title={showPwd?t("f.pwd.hide"):t("f.pwd.show")}>
                    {showPwd?"🙈":"👁"}
                  </EyeBtn>
                </PasswordWrapper>
                {mode === "signup" && password && (
                  <>
                    <PasswordStrengthBar $l={strength}/>
                    <span style={{fontSize:".65rem",color:strength>=3?"#00e676":strength>=2?"#ffc107":"#ff3b5c"}}>{STRENGTH_LABELS[strength]}</span>
                  </>
                )}
                <FieldError $show={!!errors.password}>{errors.password}</FieldError>
                {mode === "login" && (
                  <ForgotLink type="button" onClick={() => setStep("forgot-email")}>{t("forgot")}</ForgotLink>
                )}
              </Field>
                {mode === "signup" && (
  <Field>
    <FieldLabel>
      {t("ref.label")}
      {refDetected && (
        <span style={{
          marginLeft:".5rem", color:"#ffc107",
          fontSize:".62rem", letterSpacing:".02em",
          textTransform:"none"
        }}>
          ✨ {t("ref.detected")}
        </span>
      )}
    </FieldLabel>
    <FieldInput
      type="text"
      placeholder={t("ref.ph")}
      value={referralCode}
      onChange={(e) => setReferralCode(e.target.value.toUpperCase().slice(0, 12))}
      maxLength={12}
      style={{
        fontFamily: "DM Mono,monospace",
        letterSpacing: ".1em",
        background: refDetected ? "rgba(255,193,7,.06)" : undefined,
        borderColor: refDetected ? "rgba(255,193,7,.3)" : undefined,
      }}
    />
  </Field>
)}
              {mode === "signup" && (
                <CheckGroup>
                  <div>
                    <CheckRow>
                      <CheckBox type="checkbox" checked={acceptCGU} onChange={e => setAcceptCGU(e.target.checked)}/>
                      <CheckText className="required">{t("cgu.text")}</CheckText>
                    </CheckRow>
                    <FieldError $show={!!errors.cgu} style={{marginLeft:"1.5rem"}}>{errors.cgu}</FieldError>
                  </div>
                  <CheckRow>
                    <CheckBox type="checkbox" checked={acceptNews} onChange={e => setAcceptNews(e.target.checked)}/>
                    <CheckText>{t("news.text")}</CheckText>
                  </CheckRow>
                </CheckGroup>
              )}

              <ErrorBanner $show={!!globalError}>⚠ {globalError}</ErrorBanner>

              <SubmitBtn
                onClick={handleSubmit}
                disabled={loading||googleLoad||!email||!password||(mode==="signup"&&!acceptCGU)}
              >
                {loading
                  ? (mode==="signup" ? t("btn.loading.signup") : t("btn.loading.login"))
                  : mode==="login" ? t("btn.login") : t("btn.signup")
                }
              </SubmitBtn>
            </Form>
          </>
        )}
      </Modal>
    </Overlay>
  );
}
